import useSWR from 'swr'
import type { BareFetcher, SWRConfiguration, SWRResponse } from 'swr/_internal'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import useUser from 'shared/hooks/use-user'
import { addQueryParam } from 'shared/utils/add-query-param'
import { MEMBERSHIP_API } from 'modules/school/api/membership-api'
import type { LightLectureInterface } from '../types/light-lecture-interface'

export interface UseDigitalCourseLectureOptions {
  lectureId: string
  searchQuery?: string
  config?: SWRConfiguration<LightLectureInterface, Error>
}

const getLightViewLectureKey = ({
  lectureId,
  searchQuery,
}: {
  lectureId: string
  searchQuery?: string
}): string | null => {
  if (lectureId === undefined) return null

  const searchParams = new URLSearchParams()
  addQueryParam(searchParams, 'searchBy', searchQuery)

  const queryString = searchParams.toString()
  const baseUrl = `${MEMBERSHIP_API}/light-editor/lecture/${lectureId}`

  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

export function useLightViewLecture({
  config,
  lectureId,
  searchQuery,
}: UseDigitalCourseLectureOptions): SWRResponse<
  LightLectureInterface,
  unknown,
  SWRConfiguration<LightLectureInterface, unknown, BareFetcher<LightLectureInterface>> | undefined
> {
  const { user } = useUser()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    LightLectureInterface
  >({
    method: RequestMethodsEnum.get,
    badRequestHandler: () => {},
    notFoundResponseHandler: () => {},
  })

  return useSWR<LightLectureInterface>(
    () => {
      if (user) {
        return getLightViewLectureKey({ lectureId, searchQuery })
      }
      return null
    },
    fetcher,
    config,
  )
}
